import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { errorMessage } from './adminApi';

const PAGE_SIZES = [10, 5];
const DEBOUNCE_MS = 350;

/**
 * Estado de uma lista do painel guardado na URL (q, pagina, tamanho, ordem):
 * F5, voltar do detalhe e link compartilhado voltam ao mesmo ponto.
 * `load({ q, page, size, sort })` devolve { results, total, pages, note }.
 */
export default function useListState({ load, defaultSort, enabled = () => true }) {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const q = params.get('q') || '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const size = PAGE_SIZES.includes(Number(params.get('size'))) ? Number(params.get('size')) : PAGE_SIZES[0];
  const sort = params.get('sort') || defaultSort || '';

  const [input, setInput] = useState(q);
  const [state, setState] = useState({ status: 'idle', results: [], total: null, pages: 1, note: null, error: null });
  const requestRef = useRef(0);

  const update = useCallback((next) => {
    const merged = { q, page, size, sort, ...next };
    const query = new URLSearchParams();
    if (merged.q) query.set('q', merged.q);
    if (merged.page > 1) query.set('page', String(merged.page));
    if (merged.size !== PAGE_SIZES[0]) query.set('size', String(merged.size));
    if (merged.sort && merged.sort !== defaultSort) query.set('sort', merged.sort);
    const search = query.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [history, location.pathname, q, page, size, sort, defaultSort]);

  useEffect(() => {
    const request = ++requestRef.current;
    if (!enabled({ q, page, size, sort })) {
      setState({ status: 'idle', results: [], total: null, pages: 1, note: null, error: null });
      return;
    }
    setState((previous) => ({ ...previous, status: 'loading', error: null }));
    load({ q, page, size, sort })
      .then((data) => { if (request === requestRef.current) setState({ status: 'done', error: null, note: null, total: null, pages: 1, ...data }); })
      .catch((err) => {
        if (request === requestRef.current) {
          setState((previous) => ({ ...previous, status: 'error', error: errorMessage(err, 'Não foi possível carregar agora.') }));
        }
      });
  }, [q, page, size, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  // Digitar atualiza a URL depois de uma pausa (a busca roda sozinha).
  useEffect(() => {
    if (input.trim() === q) return undefined;
    const value = input.trim();
    if (value.length > 0 && value.length < 3) return undefined;
    const timer = setTimeout(() => update({ q: value, page: 1 }), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  return { q, page, size, sort, input, setInput, state, update, pageSizes: PAGE_SIZES };
}
